/**
 * RetailerAdminLayout (/retaileradmin)
 *
 * The retailer staff area's shell: a sidebar like the Trusscore admin panel
 * (Dashboard, Estimates, Analytics, Users for managers), a retailer switcher
 * when the member belongs to more than one retailer, and the active page
 * through <Outlet/>. Customer-facing enough to be translated.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, BarChart3, Users, Store, ChevronDown, MapPin } from 'lucide-react';
import { useRetailerAdmin, useRetailerContext } from './RetailerAdminContext';

interface NavItem {
  path: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
  managerOnly?: boolean;
}

const NAV: NavItem[] = [
  { path: '/retaileradmin',           labelKey: 'retaileradmin:nav.dashboard', icon: LayoutDashboard, exact: true },
  { path: '/retaileradmin/estimates', labelKey: 'retaileradmin:nav.estimates', icon: ClipboardList },
  { path: '/retaileradmin/analytics', labelKey: 'retaileradmin:nav.analytics', icon: BarChart3 },
  { path: '/retaileradmin/locations', labelKey: 'retaileradmin:nav.locations', icon: MapPin },
  { path: '/retaileradmin/users',     labelKey: 'retaileradmin:nav.users',     icon: Users, managerOnly: true },
];

const RetailerSwitcher: React.FC = () => {
  const { t } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const { switchRetailer } = useRetailerAdmin();
  const [busy, setBusy] = useState(false);
  if (ctx.memberships.length <= 1) {
    return (
      <div className="small text-muted mb-3 d-flex align-items-center gap-2">
        <Store size={14} className="flex-shrink-0" />
        <span className="text-truncate" title={ctx.retailerName}>{ctx.retailerName}</span>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <label htmlFor="ra-retailer-switch" className="form-label small text-muted mb-1">{t('actingFor')}</label>
      <div className="position-relative">
        <select
          id="ra-retailer-switch"
          className="form-select form-select-sm pe-4"
          value={ctx.retailerId}
          disabled={busy}
          onChange={async e => {
            setBusy(true);
            try { await switchRetailer(Number(e.target.value)); } finally { setBusy(false); }
          }}
        >
          {ctx.memberships.map(m => (
            <option key={m.retailerId} value={m.retailerId}>{m.retailerName}</option>
          ))}
        </select>
        <ChevronDown size={14} className="position-absolute text-muted" style={{ right: 8, top: 9, pointerEvents: 'none' }} />
      </div>
    </div>
  );
};

export const RetailerAdminLayout: React.FC = () => {
  const { t } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const location = useLocation();
  const visible = NAV.filter(item => !item.managerOnly || ctx.canManage);

  return (
    <div className="container-fluid py-3">
      <div className="row g-3">
        <div className="col-12 col-md-3 col-lg-2">
          <div className="bg-white rounded-3 shadow-sm p-3">
            <h6 className="text-uppercase text-muted small fw-bold mb-2 d-flex align-items-center gap-2">
              <Store size={14} /> {t('panel')}
            </h6>
            <RetailerSwitcher />
            <nav className="nav flex-column gap-1">
              {visible.map(item => {
                const Icon = item.icon;
                const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link d-flex align-items-center gap-2 rounded px-2 py-2 small ${
                      isActive ? 'bg-tc-yellow text-dark fw-semibold' : 'text-secondary'
                    }`}
                  >
                    <Icon size={16} />
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </nav>
            <div className="small text-muted mt-3 pt-2 border-top">
              <div>{t(`roles.${ctx.role}`)}</div>
              {ctx.allLocations ? <div>{ctx.includeChildren ? t('locations.scope.allWithChildren') : t('allLocations')}</div> : ctx.locations.length === 0 ? <div className="text-danger">{t('locations.scope.none')}</div> : ctx.locations.map(l => <div key={l.id} className="text-truncate" title={l.name}>{l.name}</div>)}
            </div>
          </div>
        </div>
        <div className="col-12 col-md-9 col-lg-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default RetailerAdminLayout;
